import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { Ambassador } from '../../../../../models/interfaces/ambassador.interface';
import { AmbassadorType } from '../../../../../models/enums/ambassador-type.enum';
import { CommissionType } from '../../../../../models/enums/commission-type.enum';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafInputComponent } from '../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafSelectLightComponent } from '../../../../../components/enfaf-select-light/enfaf-select-light.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ComboStudent } from '../../../../../models/interfaces/student.interface';
import {
  SelectOption,
  SimpleAutocompleteLightComponent
} from '../../../../../components/simple-autocomplete-light/simple-autocomplete-light.component';
import { EnfafButtonComponent } from '../../../../../components/enfaf-button/enfaf-button.component';
import { Course } from '../../../../../models/interfaces/course.interface';
import { EnfafIconComponent } from '../../../../../components/enfaf-icon/enfaf-icon.component';
import { AmbassadorCourseDto } from '../../../../../models/dto/ambassador-course.dto';
import { MatMenuModule } from '@angular/material/menu';
import { EnfafSwitchDarkComponent } from '../../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';

interface CourseOption {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-modal-edit-ambassador',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatTooltipModule,
    EnfafModalComponent,
    EnfafInputComponent,
    EnfafSelectLightComponent,
    SimpleAutocompleteLightComponent,
    EnfafButtonComponent,
    EnfafIconComponent,
    MatMenuModule,
    EnfafSwitchDarkComponent
  ],
  templateUrl: './modal-edit-ambassador.component.html',
  styleUrl: './modal-edit-ambassador.component.scss'
})
export class ModalEditAmbassadorComponent implements OnInit {
  // Component state
  editMode = false;
  ambassadorForm!: FormGroup;
  commissionForm!: FormGroup;
  showCloseAlert = false;
  isSavingAmbassador = false;
  selectedStudentOption: SelectOption | null = null;
  isCommissionFormEnabled = false;
  isCommissionCourseEditing: boolean = false;
  selectedCourseOption: SelectOption | null = null;

  // Sorting state for commissions table (signals for reactivity)
  commissionsSortField = signal<'courseName' | 'commissionType' | 'commissionValue'>('courseName');
  commissionsSortDirection = signal<'asc' | 'desc'>('asc');

  // Options for selects
  ambassadorTypeOptions = [
    { value: AmbassadorType.SELF_EMPLOYED, viewValue: 'Autónomo' },
    { value: AmbassadorType.NON_TAXABLE_INDIVIDUAL, viewValue: 'Individuo no fiscal' },
    { value: AmbassadorType.COMPANY, viewValue: 'Empresa' }
  ];

  commissionTypeOptions = [
    { value: CommissionType.PERCENTAGE, viewValue: 'Porcentaje' },
    { value: CommissionType.FIXED, viewValue: 'Importe fijo' }
  ];

  getCommisionTypeDisplayName(type: CommissionType): string {
    if (type === CommissionType.PERCENTAGE) {
      return 'Porcentaje';
    } else if (type === CommissionType.FIXED) {
      return 'Importe fijo';
    }
    return '';
  }

  // Static validators for commission values
  static percentageCommissionValidator(control: any): any {
    const value = control.value;
    if (value === null || value === '') return null;
    if (value < 0 || value > 100) {
      return { percentageRange: { message: 'El porcentaje debe estar entre 0 y 100' } };
    }
    if (value % 1 !== 0) {
      return { noDecimals: { message: 'No se permiten decimales para porcentajes' } };
    }
    return null;
  }

  static fixedCommissionValidator(control: any): any {
    const value = control.value;
    if (value === null || value === '') return null;
    if (value < 0) {
      return { negativeValue: { message: 'El valor no puede ser negativo' } };
    }
    return null;
  }

  private readonly destroyRef = inject(DestroyRef);

  get isNewUser(): boolean {
    return (
      !this.ambassadorForm.get('userId')?.value ||
      this.ambassadorForm.get('userId')?.value === '' ||
      this.ambassadorForm.get('userId')?.value === 'new'
    );
  }

  get isCommissionCourseSelected(): boolean {
    return this.commissionForm.get('courseId')?.value !== null;
  }

  get disabledCourseOptions(): SelectOption[] {
    return this.courseCommissionsFA.value.map((c: any) => ({
      id: c.courseId,
      title: c.courseId
    }));
  }

  get ambassadorEmailControl(): FormControl {
    return this.ambassadorForm.get('email') as FormControl;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalEditAmbassadorComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService
  ) {}

  async ngOnInit(): Promise<void> {
    this.initForm();
    this.initCommissionForm();
  }

  /**
   * Initialize form with data if editing
   */
  private initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    const receivedAmbassador: Ambassador = receivedData?.ambassador;

    if (receivedAmbassador) {
      this.editMode = true;
      this.selectedStudentOption = {
        id: receivedAmbassador.email,
        title: receivedAmbassador.email,
        meta: {
          id: receivedAmbassador.userId || '',
          name: receivedAmbassador.name,
          lastname: receivedAmbassador.lastname
        }
      };
    }

    this.ambassadorForm = new FormGroup({
      ambassadorId: new FormControl(receivedAmbassador?.ambassadorId || null),
      type: new FormControl(receivedAmbassador?.type || AmbassadorType.SELF_EMPLOYED, Validators.required),
      isAmbassadorProgramEnabled: new FormControl(receivedAmbassador?.isAmbassadorProgramEnabled ?? true, Validators.required),
      email: new FormControl(receivedAmbassador?.email || null, [Validators.required, Validators.email]),
      name: new FormControl(receivedAmbassador?.name || null, Validators.required),
      lastname: new FormControl(receivedAmbassador?.lastname || null, Validators.required),
      alias: new FormControl(this.editMode ? receivedAmbassador?.code || '' : this.generateAlias(), [Validators.required, Validators.maxLength(50)]),
      phonePrefix: new FormControl(receivedAmbassador?.phonePrefix || null),
      phone: new FormControl(receivedAmbassador?.phone || null, [Validators.pattern(/^\d{9}$/)]),
      courseCommissions: new FormArray([]),
      userId: new FormControl(receivedAmbassador?.userId || null)
    });

    // Disable name and lastname if editing existing ambassador
    if (this.editMode) {
      this.ambassadorForm.get('email')?.disable();
      this.ambassadorForm.get('name')?.disable();
      this.ambassadorForm.get('lastname')?.disable();
    }

    // Load existing commissions if editing
    if (receivedAmbassador?.courseCommissions) {
      receivedAmbassador.courseCommissions.forEach((commission) => {
        this.addCommission(commission);
      });

      // Apply default sort by course name ascending
      this.applySortToCommissions();
    }

    this.listenForChanges();
  }

  /** Generates a random 10-character alphanumeric alias (uppercase). */
  private generateAlias(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  }

  private initCommissionForm(): void {
    this.commissionForm = new FormGroup({
      courseId: new FormControl(null, Validators.required),
      commissionType: new FormControl(CommissionType.PERCENTAGE, Validators.required),
      commissionValue: new FormControl(10, [Validators.required, ModalEditAmbassadorComponent.percentageCommissionValidator]),
      courseName: new FormControl('')
    });

    // Listener para cambios en el tipo de comisión
    this.commissionForm
      .get('commissionType')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => {
        this.updateCommissionValidators(type);

        // Ajustar el valor según el tipo de comisión
        const currentValue = this.commissionForm.get('commissionValue')!.value;
        if (currentValue !== null && currentValue !== '') {
          if (type === CommissionType.PERCENTAGE) {
            // Si cambió a porcentaje, remover decimales
            const roundedValue = Math.round(currentValue);
            if (roundedValue !== currentValue) {
              this.commissionForm.get('commissionValue')!.setValue(roundedValue, { emitEvent: false });
            }

            // Forzar validación para mostrar errores si el valor no es válido para porcentaje
            this.commissionForm.get('commissionValue')!.markAsTouched();
            this.commissionForm.get('commissionValue')!.updateValueAndValidity();
          }
          // Si cambió a fijo, no hacer nada (mantener el valor)
        }
      });

    // Listener para validaciones en tiempo real del valor de comisión
    this.commissionForm
      .get('commissionValue')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((value) => {
        const commissionType = this.commissionForm.get('commissionType')!.value;
        if (commissionType === CommissionType.PERCENTAGE && value !== null && value !== '') {
          // Para porcentajes, redondear automáticamente si hay decimales
          const roundedValue = Math.round(value);
          if (roundedValue !== value) {
            this.commissionForm.get('commissionValue')!.setValue(roundedValue, { emitEvent: false });
          }
        }
      });

    // Inicializar validadores al cargar el formulario
    const initialCommissionType = this.commissionForm.get('commissionType')!.value;
    this.updateCommissionValidators(initialCommissionType);
  }

  private updateCommissionValidators(type: CommissionType): void {
    const control = this.commissionForm.get('commissionValue')!;

    if (type === CommissionType.PERCENTAGE) {
      control.setValidators([Validators.required, ModalEditAmbassadorComponent.percentageCommissionValidator]);
    } else if (type === CommissionType.FIXED) {
      control.setValidators([Validators.required, ModalEditAmbassadorComponent.fixedCommissionValidator]);
    }

    control.updateValueAndValidity();
  }

  /**
   * Listen for form changes (with delay to avoid marking as dirty on init)
   */
  private listenForChanges(): void {
    // Use setTimeout to ensure we only listen after initialization is complete
    setTimeout(() => {
      this.ambassadorForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.showCloseAlert = true;
      });
    }, 0);
  }

  /**
   * Get course commissions form array
   */
  get courseCommissionsFA(): FormArray {
    return this.ambassadorForm.get('courseCommissions') as FormArray;
  }

  /**
   * Get course commissions controls as FormGroups
   */
  get courseCommissionsControls(): FormGroup[] {
    return this.courseCommissionsFA.controls as FormGroup[];
  }

  /**
   * Add new commission to form array
   */
  addCommission(existingCommission?: any): void {
    let commisionType = CommissionType.PERCENTAGE;
    let commissionValue = 10;

    if (existingCommission) {
      commisionType = existingCommission.commissionType;
      commissionValue = existingCommission.commissionValue;
      if (commisionType === CommissionType.PERCENTAGE) {
        commissionValue = Math.round(commissionValue);
      }
    }

    const commissionGroup = new FormGroup({
      courseId: new FormControl(existingCommission?.courseId || null, Validators.required),
      commissionType: new FormControl(commisionType, Validators.required),
      commissionValue: new FormControl(commissionValue, [Validators.required, Validators.min(0)]),
      courseName: new FormControl(existingCommission?.courseName || '')
    });

    this.courseCommissionsFA.push(commissionGroup);

    // Apply current sorting after adding commission
    this.applySortToCommissions();
  }

  resetCommissionForm(commission?: any): void {
    this.isCommissionFormEnabled = true;
    this.commissionForm.patchValue({
      courseId: commission?.courseId || null,
      commissionType: commission?.commissionType || CommissionType.PERCENTAGE,
      commissionValue: commission?.commissionValue || 10,
      courseName: commission?.courseName || ''
    });
    if (!commission) {
      this.isCommissionCourseEditing = false;
      this.selectedCourseOption = {
        id: '',
        title: ''
      };
    } else {
      this.isCommissionCourseEditing = true;
      this.selectedCourseOption = {
        id: commission.courseId,
        title: commission.courseName
      };
    }
  }

  /**
   * Remove commission from form array
   */
  removeCommission(index: number): void {
    this.courseCommissionsFA.removeAt(index);
  }

  /**
   * Get commission icon based on type
   */
  getCommissionIcon(commissionGroup: FormGroup): { icon: string; color: string; click: () => void } {
    const type = commissionGroup.get('commissionType')?.value;
    if (type === CommissionType.FIXED) {
      return { icon: 'euro-sign', color: 'dark', click: () => {} };
    }
    return { icon: 'percent', color: 'dark', click: () => {} };
  }

  /**
   * Get error message for commission value
   */
  getCommissionValueError(commissionGroup: FormGroup): string {
    const control = commissionGroup.get('commissionValue');

    if (control?.hasError('required')) {
      return 'Este campo es requerido';
    }
    if (control?.hasError('percentageRange')) {
      return control.getError('percentageRange').message;
    }
    if (control?.hasError('noDecimals')) {
      return control.getError('noDecimals').message;
    }
    if (control?.hasError('negativeValue')) {
      return control.getError('negativeValue').message;
    }
    return '';
  }

  /**
   * Validate commission value based on type
   */
  validateCommissionValue(commissionGroup: FormGroup): void {
    const valueControl = commissionGroup.get('commissionValue');
    const type = commissionGroup.get('commissionType')?.value;

    if (!valueControl) return;

    const currentValue = valueControl.value;

    if (type === CommissionType.PERCENTAGE) {
      // For percentage, ensure it's between 0-100 and integer
      if (currentValue > 100) {
        valueControl.setValue(100);
      } else if (currentValue < 0) {
        valueControl.setValue(0);
      } else {
        // Round to integer
        const roundedValue = Math.round(currentValue);
        if (roundedValue !== currentValue) {
          valueControl.setValue(roundedValue);
        }
      }
    } else {
      // For fixed, just ensure it's not negative
      if (currentValue < 0) {
        valueControl.setValue(0);
      }
    }
  }

  /**
   * Confirm close modal with alert if changes exist
   */
  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }
    this.closeModal();
  }

  /**
   * Close modal with optional result
   */
  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  /**
   * Save ambassador (create or update)
   */
  async saveAmbassador(): Promise<void> {
    const formData = this.ambassadorForm.getRawValue();

    if (!formData.name || !formData.lastname || !formData.email || !formData.type) {
      return;
    }

    // Validate commissions
    for (const commissionGroup of this.courseCommissionsControls) {
      this.validateCommissionValue(commissionGroup);
    }

    try {
      this.isSavingAmbassador = true;

      const body = {
        ...formData,
        email: formData.email.trim().toLowerCase(),
        code: formData.alias?.trim(),
        courseCommissions: formData.courseCommissions.map((commission: any) => ({
          courseId: commission.courseId,
          commissionType: commission.commissionType,
          commissionValue: Number(commission.commissionValue)
        }))
      };
      delete body.alias;

      let response = null;
      if (!this.editMode) {
        response = await firstValueFrom(this.api.createAmbassador(body));
      } else {
        const ambassadorId = formData.ambassadorId;
        response = await firstValueFrom(this.api.updateAmbassador(ambassadorId, body));
      }

      this.isSavingAmbassador = false;
      this.closeModal({ ambassadorUpdated: true });
      this.toast.presentSuccessToast(response.message || 'Embajador guardado correctamente');
    } catch (errorResponse: any) {
      this.isSavingAmbassador = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando el embajador');
    }
  }

  fetchStudents = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      firstValueFrom(
        this.api.fetchReducedStudents({
          page: page + 1,
          limit: pageSize,
          search: search,
          hasAmbassadors: false
        })
      )
        .then((response) => {
          const mappedData: SelectOption[] = response.data.map((student: ComboStudent) => ({
            id: student.email,
            title: student.email,
            selected: false,
            meta: {
              id: student.id,
              name: student.name,
              lastname: student.lastname
            }
          }));

          observer.next({
            data: mappedData,
            total: response.pagination.total
          });
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  };

  handleSelectStudent(selected: SelectOption | null): void {
    const actualName = this.ambassadorForm.get('name')?.value;
    const actualLastname = this.ambassadorForm.get('lastname')?.value;

    if (!selected) {
      this.ambassadorForm.get('name')?.enable();
      this.ambassadorForm.get('lastname')?.enable();
      this.ambassadorForm.patchValue({
        userId: null,
        email: null,
        name: !this.isNewUser ? null : actualName,
        lastname: !this.isNewUser ? null : actualLastname
      });
      return;
    }

    // If it's a new user (id === 'new'), enable name and lastname fields
    if (selected.id === 'new') {
      this.ambassadorForm.get('name')?.enable();
      this.ambassadorForm.get('lastname')?.enable();
      this.ambassadorForm.patchValue({
        userId: null,
        email: selected.title,
        name: !this.isNewUser ? null : actualName,
        lastname: !this.isNewUser ? null : actualLastname
      });
    } else {
      this.ambassadorForm.patchValue({
        userId: selected.id,
        email: selected.title,
        name: selected.meta?.name || actualName,
        lastname: selected.meta?.lastname || actualLastname
      });

      // If it's an existing user, disable name and lastname fields
      this.ambassadorForm.get('name')?.disable();
      this.ambassadorForm.get('lastname')?.disable();
    }
  }

  fetchCourses = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    const notIds = this.courseCommissionsFA.value
      .map((c: any) => c.courseId)
      .filter((id: string) => id !== this.commissionForm.get('courseId')?.value);

    return new Observable((observer) => {
      firstValueFrom(
        this.api.fetchAmmbassadorCourses({
          page: page,
          pageSize,
          search: search,
          notIds
        })
      )
        .then((response) => {
          const mappedData: SelectOption[] = response.courses.map((course: AmbassadorCourseDto) => ({
            id: course.courseId,
            title: course.name,
            selected: false
          }));

          observer.next({
            data: mappedData,
            total: response.pagination.totalItems
          });
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  };

  handleSelectCourse(selected: SelectOption | null): void {
    if (!selected) {
      this.commissionForm.patchValue({
        courseId: null,
        courseName: ''
      });
      return;
    }

    this.commissionForm.patchValue({
      courseId: selected.id,
      courseName: selected.title
    });

    this.selectedCourseOption = selected;
  }

  submitCommisionForm(): void {
    if (this.commissionForm.invalid) {
      return;
    }

    if (this.isCommissionCourseEditing) {
      const index = this.courseCommissionsFA.controls.findIndex(
        (group) => group.get('courseId')?.value === this.commissionForm.get('courseId')?.value
      );
      if (index !== -1) {
        this.courseCommissionsFA.at(index).patchValue(this.commissionForm.getRawValue());
      }
      this.resetCommissionForm();
      return;
    }
    this.addCommission(this.commissionForm.getRawValue());
    this.resetCommissionForm();
  }

  closeCommissionForm(): void {
    this.resetCommissionForm();
    this.isCommissionFormEnabled = false;
  }

  /**
   * Handle column sort for commissions table (3 states: none → asc → desc → none)
   */
  onSortCommissions(field: 'courseName' | 'commissionType' | 'commissionValue'): void {
    if (this.commissionsSortField() === field) {
      // Same field: cycle through states
      if (this.commissionsSortDirection() === 'asc') {
        this.commissionsSortDirection.set('desc');
      } else if (this.commissionsSortDirection() === 'desc') {
        // Reset to no sorting
        this.commissionsSortField.set('courseName');
        this.commissionsSortDirection.set('asc');
      }
    } else {
      // New field: start with ascending
      this.commissionsSortField.set(field);
      this.commissionsSortDirection.set('asc');
    }

    this.applySortToCommissions();
  }

  /**
   * Apply sorting to commissions FormArray
   */
  private applySortToCommissions(): void {
    const commissionsArray = this.courseCommissionsFA;
    const sortedControls = [...commissionsArray.controls].sort((a, b) => {
      const aValue = a.get(this.commissionsSortField())?.value;
      const bValue = b.get(this.commissionsSortField())?.value;

      let comparison = 0;

      if (this.commissionsSortField() === 'courseName') {
        // String comparison
        comparison = (aValue || '').toLowerCase().localeCompare((bValue || '').toLowerCase());
      } else if (this.commissionsSortField() === 'commissionType') {
        // String comparison (percentage vs fixed)
        const aType = aValue === 'percentage' ? 'Porcentaje' : 'Fijo';
        const bType = bValue === 'percentage' ? 'Porcentaje' : 'Fijo';
        comparison = aType.localeCompare(bType);
      } else if (this.commissionsSortField() === 'commissionValue') {
        // Numeric comparison
        comparison = (aValue || 0) - (bValue || 0);
      }

      return this.commissionsSortDirection() === 'asc' ? comparison : -comparison;
    });

    // Clear and repopulate FormArray with sorted controls
    commissionsArray.clear();
    sortedControls.forEach((control) => commissionsArray.push(control));
  }

  /**
   * Get sort icon for commission table column header
   */
  getCommissionSortIcon(field: 'courseName' | 'commissionType' | 'commissionValue'): string {
    if (this.commissionsSortField() !== field) {
      return 'order_up_down';
    }
    return this.commissionsSortDirection() === 'asc' ? 'order_up' : 'order_down';
  }

  /**
   * Check if commission column is currently sorted
   */
  isCommissionSorted(field: 'courseName' | 'commissionType' | 'commissionValue'): boolean {
    return this.commissionsSortField() === field;
  }
}
